/**
 * @file TypoPartFields.tsx
 * @description Поля одной части текста элемента (phase19.16, партия SB120,
 * этап 3а; uiux-sb120-stage3 §2.1; данные db-design-sb120-stage3 §2):
 *
 * ```
 * Цвет текста                                  ↺ авто
 * [Аa Авто][●Текст][●Второй][●Акцент][●На тёмном][⌖ #F4EBDD]
 * ! Контраст 3,1:1 — читается только крупный текст. [Взять читаемый]
 * Шрифт  [ По роли сайта | Свой шрифт ] · Начертание · Регистр
 * Размер ├──────●─────┤ [ 56 ] px   [H3][H2][H1]   ↺ 48 px (H1)
 *  на телефоне 36 px                        Своё ›   ← DeviceValueRow
 * Разрядка ├●─────┤ [ 0 ] em
 * ```
 *
 * Значение — часть `typo.<key>`: нет ключа — «как у роли» / «Авто». Правка —
 * `onPatch` (ключ со значением `undefined` = снять, «↺»). Размер ПК — px с
 * дробями до сотых; на телефоне — правило shared `phoneFontSize` (до 16 px
 * как на ПК, крупнее — 8 + ПК/2) или своё `sizePhone`. Ползунки — живой
 * предпросмотр на канве по пути `<владелец>.typo.<часть>.<поле>`.
 */

import {
  DEFAULT_ELEMENT_FONT,
  ELEMENT_TYPO_LIMITS,
  SITE_TEXT_COLOR_ROLES,
  phoneFontSize,
  resolveFontRole,
  type FontPart,
  type SectionElementWarning,
  type SiteFontRegistry,
  type SiteTextColor,
  type SiteTheme,
  type TypoPart,
} from "@chaiberi/shared";

import { FieldHint } from "../../../ui/settings-groups";
import { NoticeStrip } from "../../../ui/NoticeStrip";
import { ColorField, type ColorTextValue } from "../../../site-fields/ColorField";
import { FontField, type FontFieldPatch } from "../../../site-fields/FontField";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import type { NumberPreset } from "../../../site-fields/NumberPresets";
import { DeviceValueRow } from "../fields/DeviceValueRow";
import { useSectionForm } from "../section-form-context";
import type { TypoPartPatch } from "../../../../lib/site-builder/element-typo";
import { elementWarningText, elementWarningsUnder } from "../../../../lib/site-builder/element-warnings";
import { phoneSizeSummary, type TypoBase } from "../../../../lib/site-builder/typo-base";
import { TEXT_COLOR_ROLE_LABELS } from "../../../../lib/site-fields/text-color-labels";

type Role = (typeof SITE_TEXT_COLOR_ROLES)[number];
const PX = { factor: 1, decimals: 2 } as const;

export interface TypoPartFieldsProps {
  part: FontPart;
  value: TypoPart | undefined;
  onPatch: (patch: TypoPartPatch) => void;
  /** Путь части в payload: `columns.items.0.elements.2.typo.main`. */
  path: string;
  base: TypoBase;
  theme: SiteTheme;
  registry: SiteFontRegistry;
  /** Фон под текстом для контраста: `#hex`, `"photo"` или неизвестен. */
  elementBg?: string;
  colorAutoLabel?: string;
  /** Цвет «Авто», если особый — для меры контраста. */
  colorAuto?: string;
  colorHint?: string | null;
  sizePresets?: readonly NumberPreset[];
  /** Предупреждения секции (мелкий H1, число шрифтов). */
  warnings: readonly SectionElementWarning[];
  familiesOnPage?: number;
}

export function TypoPartFields(props: TypoPartFieldsProps) {
  const { part, value, onPatch, path, base, theme, registry } = props;
  const { preview } = useSectionForm();
  const color: ColorTextValue<Role> = value?.color ?? { kind: "auto" };
  const setColor = (next: ColorTextValue<Role>) => onPatch({ color: next.kind === "auto" ? undefined : (next as SiteTextColor) });
  const sendPreview = (patch: FontFieldPatch) => Object.entries(patch).forEach(([field, v]) => preview(`${path}.${field}`, v));
  const phoneInherit = value?.size !== undefined ? phoneFontSize(value.size) : base.phone;
  const h1 = elementWarningsUnder(props.warnings, path).filter((w) => w.code === "h1_small");
  const L = ELEMENT_TYPO_LIMITS.size;

  const phoneRow = (
    <DeviceValueRow summary={phoneSizeSummary(value, base)} own={value?.sizePhone !== undefined} label="Размер на телефоне">
      <NumberSliderField
        label="Размер на телефоне"
        value={value?.sizePhone}
        inherit={{ value: phoneInherit, label: "по правилу" }}
        min={L.min}
        max={L.max}
        step={1}
        unit="px"
        display={PX}
        hint="До 16 px — как на ПК, крупнее — 8 + половина размера на ПК."
        onChange={(sizePhone) => onPatch({ sizePhone })}
        onPreview={(v) => preview(`${path}.sizePhone`, v)}
      />
    </DeviceValueRow>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <ColorField
          mode="text"
          label="Цвет текста"
          value={color}
          roles={SITE_TEXT_COLOR_ROLES}
          labels={TEXT_COLOR_ROLE_LABELS}
          colors={theme.colors}
          elementBg={props.elementBg}
          autoLabel={props.colorAutoLabel}
          autoColor={props.colorAuto}
          onReset={value?.color ? () => setColor({ kind: "auto" }) : undefined}
          onChange={setColor}
        />
        {props.colorHint ? <FieldHint className="mt-0">{props.colorHint}</FieldHint> : null}
      </div>
      <FontField
        value={value?.font ?? DEFAULT_ELEMENT_FONT}
        weight={value?.weight}
        case={value?.case}
        size={value?.size}
        tracking={value?.tracking}
        theme={theme}
        registry={registry}
        autoRole={resolveFontRole(part.role, theme)}
        inherit={{ sizePx: base.desktop, label: base.label, sizeText: base.text, trackingEm: base.tracking }}
        sizePresets={props.sizePresets}
        sizeExtra={phoneRow}
        familiesOnPage={props.familiesOnPage}
        onChange={(patch) => onPatch(patch)}
        onPreview={sendPreview}
      />
      {h1.map((w) => (
        <NoticeStrip key={w.path} tone="warning">
          {elementWarningText(w)}
        </NoticeStrip>
      ))}
    </div>
  );
}
